<template>
  <v-dialog
    :model-value="modelValue"
    max-width="500"
    :scrim="true"
    class="mention-dialog-overlay"
    @update:model-value="onDialogToggle">
    <v-card class="mention-dialog">
      <template v-if="!submitted">
        <v-card-title class="mention-dialog__title">Mention a teammate or group</v-card-title>
        <v-card-text class="mention-dialog__body">
          <div class="mention-dialog__context">
            <span class="mention-dialog__context-label">{{ contextLabel }}</span>
            <span class="mention-dialog__key">{{ displayKey }}</span>
          </div>

          <v-autocomplete
            v-model="selectedKeys"
            :items="mentionItems"
            item-title="title"
            item-value="value"
            label="Who should take a look?"
            variant="outlined"
            multiple
            chips
            closable-chips
            autofocus
            :loading="teammatesLoading"
            :menu-props="{ contentClass: 'mention-dialog__menu' }"
            no-data-text="No teammates yet — ask your admin to invite your team."
            hide-details="auto"
            class="mention-dialog__recipients">
            <template #chip="{ props: chipProps, item }">
              <v-chip v-bind="chipProps" size="small" class="mention-dialog__chip">
                <template #prepend>
                  <span class="mention-dialog__avatar mention-dialog__avatar--chip" :style="avatarStyle(item.raw)">
                    <v-icon v-if="item.raw.icon" size="10">{{ item.raw.icon }}</v-icon>
                    <template v-else>{{ item.raw.initials }}</template>
                  </span>
                </template>
                {{ item.raw.title }}
              </v-chip>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle">
                <template #prepend>
                  <span class="mention-dialog__avatar" :style="avatarStyle(item.raw)">
                    <v-icon v-if="item.raw.icon" size="14">{{ item.raw.icon }}</v-icon>
                    <template v-else>{{ item.raw.initials }}</template>
                  </span>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-textarea
            v-model="message"
            label="Message"
            placeholder="What do they need to look at?"
            variant="outlined"
            rows="3"
            counter
            maxlength="500"
            class="mt-4"
            hide-details="auto"
            @keydown.enter="onMessageEnter" />

          <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mt-4">
            {{ errorMsg }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="mention-dialog__actions">
          <v-spacer />
          <v-btn variant="text" :disabled="loading" @click="onCancel">Cancel</v-btn>
          <v-btn color="primary" :loading="loading" :disabled="!canSend" @click="onSend">Send</v-btn>
        </v-card-actions>
      </template>

      <template v-else>
        <v-card-text class="mention-dialog__success">
          <div class="mention-dialog__success-icon">
            <v-icon size="28">mdi-check</v-icon>
          </div>
          <div class="mention-dialog__success-title">{{ successTitle }}</div>
          <div class="mention-dialog__success-text">They'll see it in their inbox.</div>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import mentionsService from "@/services/mentionsService";
import { useGroupsStore, useInboxStore } from "@/stores";
import { AVATAR_COLORS, TAG_COLORS } from "@/constants/tagColors";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  target: {
    type: Object,
    required: true,
    validator: (t) => ["prior_auth", "intake_document", "intake_order"].includes(t?.kind),
  },
});

const emit = defineEmits(["update:modelValue", "sent"]);

const inboxStore = useInboxStore();
const groupsStore = useGroupsStore();

const selectedKeys = ref([]);
const message = ref("");
const loading = ref(false);
const teammatesLoading = ref(false);
const errorMsg = ref("");
const submitted = ref(false);
const successTitle = ref("");
let closeTimer = null;

const MENTION_MESSAGE_LIMIT = 500;

const CONTEXT_LABELS = {
  prior_auth: "On order",
  intake_document: "On document",
  intake_order: "On intake order",
};

const contextLabel = computed(() => CONTEXT_LABELS[props.target.kind]);
const displayKey = computed(() =>
  props.target.kind === "intake_order" ? `#${props.target.key}` : String(props.target.key)
);

function initialsOf(user) {
  const first = (user.firstName || "").trim();
  const last = (user.lastName || "").trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return initials || (user.email || "?").charAt(0).toUpperCase();
}

const teammateItems = computed(() =>
  inboxStore.teammates.map((user) => {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email;
    return {
      title: fullName,
      value: `user-${user.id}`,
      id: user.id,
      kind: "user",
      subtitle: user.email,
      initials: initialsOf(user),
      color: AVATAR_COLORS[user.id % AVATAR_COLORS.length],
    };
  })
);

const groupItems = computed(() =>
  groupsStore.groups.map((group) => ({
    title: group.name,
    value: `group-${group.id}`,
    id: group.id,
    kind: "group",
    subtitle: `${group.memberCount} ${group.memberCount === 1 ? "person" : "people"}`,
    icon: "mdi-account-group",
    color: TAG_COLORS[group.color] || TAG_COLORS.grey,
  }))
);

const everyoneItems = computed(() => {
  const count = teammateItems.value.length;
  if (!count) return [];
  return [
    {
      title: "Everyone",
      value: "everyone",
      kind: "everyone",
      subtitle: `${count} ${count === 1 ? "person" : "people"}`,
      icon: "mdi-account-multiple",
      color: TAG_COLORS.charcoal,
    },
  ];
});

const assignedItems = computed(() => {
  if (!teammateItems.value.length) return [];
  return [
    {
      title: "Assigned",
      value: "assigned",
      kind: "assigned",
      subtitle: "People assigned to this case",
      icon: "mdi-account-check",
      color: TAG_COLORS.charcoal,
    },
  ];
});

const mentionItems = computed(() => [
  ...assignedItems.value,
  ...everyoneItems.value,
  ...groupItems.value,
  ...teammateItems.value,
]);

const selectedItems = computed(() =>
  selectedKeys.value.map((key) => mentionItems.value.find((item) => item.value === key)).filter(Boolean)
);

function avatarStyle(item) {
  return {
    background: `color-mix(in srgb, ${item.color} 14%, white)`,
    color: item.color,
  };
}

const canSend = computed(() => selectedKeys.value.length > 0 && message.value.trim().length > 0);

watch(
  () => props.modelValue,
  async (open) => {
    clearCloseTimer();
    if (!open) return;
    selectedKeys.value = [];
    message.value = "";
    errorMsg.value = "";
    submitted.value = false;
    teammatesLoading.value = true;
    try {
      await Promise.all([inboxStore.fetchTeammates(), groupsStore.fetchGroups()]);
    } catch {
      errorMsg.value = "Couldn't load your teammates. Please try again.";
    } finally {
      teammatesLoading.value = false;
    }
  }
);

function clearCloseTimer() {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

onBeforeUnmount(clearCloseTimer);

function onDialogToggle(value) {
  if (!loading.value) emit("update:modelValue", value);
}

function onCancel() {
  emit("update:modelValue", false);
}

function onMessageEnter(event) {
  if (!(event.metaKey || event.ctrlKey)) return;
  event.preventDefault();
  if (canSend.value && !loading.value) onSend();
}

function taggedMessage(text, tags) {
  const missing = tags.filter((tag) => !new RegExp(`@${tag}\\b`, "i").test(text));
  if (!missing.length) return text;
  return `${missing.map((tag) => `@${tag}`).join(" ")} ${text}`.slice(0, MENTION_MESSAGE_LIMIT);
}

async function onSend() {
  if (!canSend.value) return;
  loading.value = true;
  errorMsg.value = "";
  try {
    const tags = selectedItems.value.filter((item) => ["assigned", "everyone"].includes(item.kind));
    const assigned = tags.some((item) => item.kind === "assigned");
    const payload = {
      recipientIds: selectedItems.value.filter((item) => item.kind === "user").map((item) => item.id),
      groupIds: selectedItems.value.filter((item) => item.kind === "group").map((item) => item.id),
      everyone: tags.some((item) => item.kind === "everyone"),
      assigned,
      message: taggedMessage(
        message.value.trim(),
        tags.map((item) => item.title)
      ),
    };
    if (props.target.kind === "prior_auth") payload.orderKey = props.target.key;
    if (props.target.kind === "intake_document") payload.intakeDocumentKey = props.target.key;
    if (props.target.kind === "intake_order") payload.intakeOrderId = props.target.key;
    const result = await mentionsService.createMention(payload);
    if (assigned && result?.created === 0) {
      errorMsg.value = "No one assigned to this case can be notified here.";
      return;
    }

    const first = selectedItems.value[0];
    const others = selectedItems.value.length - 1;
    const who = others > 0 ? `${first?.title} +${others}` : first?.title;
    successTitle.value = `Mentioned ${who} on ${displayKey.value}`;
    submitted.value = true;
    emit("sent");
    clearCloseTimer();
    closeTimer = setTimeout(() => emit("update:modelValue", false), 1400);
  } catch (e) {
    errorMsg.value = e?.message || "Failed to send. Please try again.";
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.mention-dialog {
  border-radius: 16px !important;
  box-shadow: $lh_shadow_lg !important;
  overflow: hidden;
}

.mention-dialog__title {
  font-size: $lh_font_size_large;
  font-weight: $lh_font_weight_bold;
  padding: 20px 24px 8px;
}

.mention-dialog__body {
  padding: 8px 24px 16px;
}

.mention-dialog__context {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.mention-dialog__context-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
}

.mention-dialog__key {
  font-family: $lh_font_mono;
  font-size: 13px;
  color: $lh_color_navy_dark;
  background: rgba($lh_color_navy, 0.06);
  border-radius: 4px;
  padding: 1px 6px;
}

.mention-dialog__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 10px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.mention-dialog__avatar--chip {
  width: 18px;
  height: 18px;
  margin-right: 4px;
  font-size: 8.5px;
}

.mention-dialog__actions {
  padding: 8px 24px 20px;
}

.mention-dialog__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px 36px;
}

.mention-dialog__success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba($lh_sidebar_accent, 0.12);
  color: $lh_sidebar_accent;
  margin-bottom: 14px;
}

.mention-dialog__success-title {
  font-size: 16px;
  font-weight: $lh_font_weight_bold;
  color: $lh_color_navy_dark;
  margin-bottom: 4px;
}

.mention-dialog__success-text {
  font-size: 13px;
  color: $lh_color_blue_gray;
}
</style>

<style lang="scss">
.mention-dialog-overlay .v-overlay__scrim {
  backdrop-filter: blur(4px);
}
</style>
